import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Download, Mail, Sparkles } from "lucide-react";
import { roles } from "../data";
import profilePhoto from "../assets/projects/mypic.png";

const particles = Array.from({ length: 22 }, (_, index) => ({
  id: index,
  left: `${(index * 37) % 100}%`,
  top: `${(index * 19) % 88}%`,
  delay: `${(index % 7) * 0.45}s`,
}));

export default function Hero() {
  const [roleIndex, setRoleIndex] = useState(0);
  const [letterCount, setLetterCount] = useState(0);
  const activeRole = roles[roleIndex];

  useEffect(() => {
    if (letterCount < activeRole.length) {
      const timeout = setTimeout(() => setLetterCount((value) => value + 1), 75);
      return () => clearTimeout(timeout);
    }

    const timeout = setTimeout(() => {
      setLetterCount(0);
      setRoleIndex((value) => (value + 1) % roles.length);
    }, 1400);

    return () => clearTimeout(timeout);
  }, [activeRole, letterCount]);

  const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" className="relative min-h-screen overflow-hidden pt-24 sm:pt-28">
      <div className="noise-overlay pointer-events-none absolute inset-0 opacity-30" />
      <div className="blob absolute left-6 top-32 h-56 w-56 rounded-full bg-cyan-400/20" />
      <div className="blob absolute right-10 top-24 h-72 w-72 rounded-full bg-purple-500/[0.18]" />
      <div className="blob absolute bottom-16 left-1/2 h-52 w-52 rounded-full bg-blue-500/[0.16]" />
      {particles.map((particle) => (
        <span
          key={particle.id}
          className="particle absolute h-1.5 w-1.5 rounded-full bg-cyan-200/70"
          style={{ left: particle.left, top: particle.top, animationDelay: particle.delay }}
        />
      ))}

      <div className="section-shell relative grid min-h-[calc(100vh-6rem)] items-center gap-8 sm:gap-12 lg:grid-cols-[1.08fr_0.92fr]">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, ease: "easeOut" }}
        >
          <div className="mb-5 inline-flex max-w-full items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-2 text-xs font-semibold text-cyan-100 backdrop-blur-xl sm:mb-6 sm:px-4 sm:text-sm">
            <Sparkles className="shrink-0" size={16} />
            Internship / job application portfolio
          </div>

          <h1 className="max-w-4xl font-display text-3xl font-black leading-tight text-white sm:text-5xl lg:text-7xl">
            Сайн байна уу, би <span className="gradient-text">Төлепберген Нурлан</span>
          </h1>

          <p className="mt-4 text-base font-semibold leading-7 text-slate-200 sm:mt-5 sm:text-lg">
            Computer Science Student | Full Stack Developer | AI & Backend Enthusiast
          </p>

          <p className="typing-caret mt-4 min-h-8 text-lg font-extrabold text-cyan-200 sm:text-xl">
            {activeRole.slice(0, letterCount)}
          </p>

          <p className="mt-5 max-w-3xl text-sm leading-7 text-slate-300 sm:mt-6 sm:text-lg sm:leading-8">
            Би ШУТИС-ийн Мэдээлэл, Холбооны Технологийн Сургуульд Компьютерын ухааны чиглэлээр суралцдаг.
            Java, Python, React, Spring Boot, frontend/backend хөгжүүлэлт болон microservice architecture чиглэлээр
            төсөл хийж, өөрийгөө хөгжүүлж байна.
          </p>

          <div className="mt-7 grid gap-3 sm:mt-9 sm:flex sm:flex-wrap">
            <button onClick={() => scrollToSection("projects")} className="btn-glow w-full sm:w-auto">
              Төслүүд харах <ArrowDown size={18} />
            </button>
            <a href="/cv-nurlan.pdf" download="CV-Төлепберген-Нурлан.pdf" className="btn-soft w-full sm:w-auto">
              CV татах <Download size={18} />
            </a>
            <button onClick={() => scrollToSection("contact")} className="btn-soft w-full sm:w-auto">
              Холбоо барих <Mail size={18} />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          className="relative mx-auto w-full max-w-sm sm:max-w-md"
        >
          <motion.div
            animate={{ y: [0, -16, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
            className="glass-card relative overflow-hidden p-4 sm:p-6"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-300/10 via-transparent to-purple-400/[0.15]" />
            <div className="relative">
              <div className="relative mx-auto aspect-[3/4] max-h-[27rem] max-w-[18rem] overflow-hidden rounded-3xl border border-white/10 bg-slate-900/80 shadow-purple-glow sm:max-w-[22rem]">
                <img
                  src={profilePhoto}
                  alt="Төлепберген Нурлан"
                  className="h-full w-full object-cover object-center"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 via-slate-950/45 to-transparent p-4 sm:p-5">
                  <p className="text-base font-black text-white sm:text-lg">Төлепберген Нурлан</p>
                  <p className="mt-1 text-sm font-semibold text-cyan-100">Full Stack Developer</p>
                  <p className="mt-1 text-sm text-slate-300">Ulaanbaatar, Mongolia</p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3 sm:p-4">
                  <p className="text-xl font-black text-white sm:text-2xl">3-р</p>
                  <p className="mt-1 text-slate-400">курс</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3 sm:p-4">
                  <p className="text-xl font-black text-white sm:text-2xl">GPA 3.4</p>
                  <p className="mt-1 text-slate-400">одоогийн</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
